import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, tap } from 'rxjs/operators';
import { OverviewDataSource } from '../../../overview/overview-data-source';
import { PermissionService } from '../../services/permission.service';
import { ImageDialogComponent, ImageDialogData } from '../image-dialog/image-dialog.component';
import { openQuoteCreationDialog } from '../quote-creation-dialog/quote-creation-dialog.component';
import { OverlayDialogHelper } from '../utils/overlay-dialog.helper';
import { QuoteEditItem, QuoteItem } from '../../../overview/overview.data.service';
import { openQuoteDeleteDialog } from '../quote-delete-dialog/quote-delete-dialog.component';
import { QuoteConversion, QuoteService } from '../../services/quote.service';
import { openTemplateUnitTestEditDialog } from '../template-unit-test-edit-dialog/template-unit-test-edit-dialog.component';
import { SnackBarService } from '../../services/snack-bar.service';

@Component({
    selector: 'app-templates-table',
    templateUrl: './templates-table.component.html',
    styleUrls: ['./templates-table.component.scss'],
    standalone: false
})
export class TemplatesTableComponent implements OnInit, AfterViewInit, OnDestroy {
    private helper: OverlayDialogHelper<ImageDialogComponent, ImageDialogData>;

    public columnsToDisplayForCustomer = ['title', 'schemePreview', 'description', 'actions'];
    public columnsToDisplayForEmployee = ['title', 'schemePreview', 'description', 'createdDate', 'actions'];
    public columnsToDisplay;

    @ViewChild(MatSort, { static: true })
    public sort: MatSort;

    @ViewChild(MatPaginator, { static: true })
    public paginator: MatPaginator;

    @Input()
    public dataSource: OverviewDataSource<any, any>;

    @Output()
    public useTemplate = new EventEmitter<any>();

    @Output()
    public pagingError = new EventEmitter<any>();
    @Output()
    public sortError = new EventEmitter<any>();

    @Input()
    public isAdmin: boolean;

    @Output() public reloadTemplates = new EventEmitter();

    constructor(
        @Inject(DOCUMENT) document: Document,
        private translationService: TranslateService,
        private dialog: MatDialog,
        private permissionService: PermissionService,
        private quoteService: QuoteService,
        private snackBarService: SnackBarService
    ) {
        this.helper = new OverlayDialogHelper<ImageDialogComponent, ImageDialogData>(
            document,
            dialog,
            ImageDialogComponent,
            'image-dialog'
        );
    }

    ngOnInit(): void {
        this.dataSource.init(this.paginator, this.sort);
        this.translatePaginator();
        this.setColumnsToDisplay();
    }

    private setColumnsToDisplay() {
        this.columnsToDisplay = this.permissionService.isUserAnEmployee
            ? this.columnsToDisplayForEmployee
            : this.columnsToDisplayForCustomer;
    }

    onSelectTemplate(quote: QuoteItem) {
        openQuoteCreationDialog(
            this.dialog,
            this.translationService.instant('OVERVIEW.DIALOG.USE_TEMPLATE.TITLE'),
            this.translationService.instant('OVERVIEW.DIALOG.USE_TEMPLATE.CONFIRM'),
            this.translationService.instant('COMMON.DIALOG.CANCEL')
        ).subscribe((customerPurchaseNumber) => {
            if (customerPurchaseNumber) {
                this.useTemplate.emit({
                    ...quote,
                    customerPurchaseNumber,
                });
            }
        });
    }

    public onImageHover(event: MouseEvent, obj) {
        this.helper.handleEvent(event, {
            quoteId: obj.id,
            imageUrl: obj.schemePreview,
        });
    }

    private translatePaginator() {
        const intl = this.paginator._intl;
        intl.itemsPerPageLabel = this.translationService.instant('OVERVIEW.TABLE.PAGINATOR.LABEL.ITEMS_PER_PAGE');
        intl.getRangeLabel = (page: number, pageSize: number, length: number) => {
            const start = page * pageSize;
            const end = start < length ? Math.min(start + pageSize, length) : start + pageSize;
            return this.translationService.instant('OVERVIEW.TABLE.PAGINATOR.LABEL.RANGE', {
                page: start + 1,
                pageSize: end,
                length,
            });
        };
        intl.changes.next();
    }

    public ngAfterViewInit(): void {
        // listen to paging events
        this.paginator.page
            .pipe(
                mergeMap(() => this.dataSource.loadOverviewData({ sortBy: 'title', sortOrder: 'asc', pageSize: 10 })),
                catchError(() => {
                    this.pagingError.emit();
                    return EMPTY;
                })
            )
            .subscribe();

        // listen to sort events
        this.sort.sortChange
            .pipe(
                tap(() => this.dataSource.resetPaging()), // reset to first page when sorting
                mergeMap(() => this.dataSource.loadOverviewData({ sortBy: 'title', sortOrder: 'asc', pageSize: 10 })),
                catchError(() => {
                    this.sortError.emit();
                    return EMPTY;
                })
            )
            .subscribe();
    }

    public onDeleteClick(quote: QuoteItem) {
        openQuoteDeleteDialog(
            this.dialog,
            this.translationService.instant('OVERVIEW.DIALOG.DELETE_TEMPLATE.TITLE'),
            `${this.translationService.instant('OVERVIEW.DIALOG.DELETE_TEMPLATE.MESSAGE')} ${
                quote.customerPurchaseNumber
            }`,
            this.translationService.instant('COMMON.DIALOG.CONFIRM'),
            this.translationService.instant('COMMON.DIALOG.CANCEL')
        )
            .pipe(
                filter((confirmed) => !!confirmed),
                switchMap(() => this.quoteService.convertQuote({ id: quote.id, type: 'quote' }))
            )
            .subscribe({
                next: () => {
                    this.snackBarService.openSnackBar({
                        message: this.translationService.instant('COMMON.OVERVIEW.DELETE.SUCCESS'),
                        isFailure: false,
                    });
                    this.reloadTemplates.emit();
                },
            });
    }

    public onEditClick(quote: QuoteEditItem) {
        openTemplateUnitTestEditDialog(this.dialog, quote)
            .pipe(
                filter((conversion: QuoteConversion | null | undefined) => !!conversion),
                switchMap((conversion: QuoteConversion) => this.quoteService.convertQuote(conversion))
            )
            .subscribe({
                next: () => {
                    this.snackBarService.openSnackBar({
                        message: this.translationService.instant('COMMON.OVERVIEW.EDIT.SUCCESS'),
                        isFailure: false,
                    });
                    this.reloadTemplates.emit();
                },
            });
    }

    public ngOnDestroy(): void {
        this.helper.destroy();
    }
}
